import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Container, 
  Typography, 
  Button 
} from '@mui/material';
import { CheckCircle, Favorite } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Body from '../components/Body';
import { track } from '../helpers/mixpanel';

export default function ThankYou() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const decision = searchParams.get('decision'); // 'accept', 'decline', 'cancel', 'time_decided', etc.
  const datesCount = parseInt(searchParams.get('dates') || '0');
  const selectedTime = searchParams.get('selectedTime');
  
  // Determine the scenario
  const isAccept = decision === 'accept';
  const isDecline = decision === 'decline';
  const isCancel = decision === 'cancel';
  const isTimeDecided = decision === 'time_decided' || decision === 'time_selected';
  const hasSelectedDates = datesCount > 0;

  const handleBackToHome = () => {
    track('back_to_home_from_thankyou', { decision, datesCount });
    navigate('/');
  };

  // Format the selected time for display
  const formatSelectedTime = (timeString: string) => {
    if (!timeString) return '';
    
    try {
      const date = new Date(timeString);
      const weekday = date.toLocaleDateString(i18n.language, { weekday: 'long' });
      const day = date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' });
      const time = date.toLocaleTimeString(i18n.language, { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      
      const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
      return `${capitalizedWeekday}, ${day} kl. ${time}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  // Get content based on scenario
  const getContent = () => {
    if (isTimeDecided) {
      // User confirmed a specific time - the date is now decided!
      const formattedTime = selectedTime ? formatSelectedTime(selectedTime) : '';
      const baseMessage = t('thankyou.time_decided.message', 'Vi har bekräftat er träff och kommer att skicka ett meddelande till den andra personen. Ni kommer båda att få information om tid och plats för er träff inom kort!');
      const messageWithTime = formattedTime 
        ? `${baseMessage}<br/><br/>📅 <strong>${formattedTime}</strong>`
        : baseMessage;
      
      return {
        icon: <Favorite sx={{ fontSize: '4rem', color: '#FF6B9D', mb: 2 }} />,
        title: t('thankyou.time_decided.title', 'Perfekt! 🎉'),
        subtitle: t('thankyou.time_decided.subtitle', 'Träffen är nu bestämd!'),
        message: messageWithTime
      };
    } else if (isAccept && hasSelectedDates) {
      // User accepted and provided dates - exciting!
      return {
        icon: <Favorite sx={{ fontSize: '4rem', color: '#FF6B9D', mb: 2 }} />,
        title: t('thankyou.accept_with_dates.title', 'Så kul! 💕'),
        subtitle: t('thankyou.accept_with_dates.subtitle', 'Vi är så glada att du vill träffa din match!'),
        message: t('thankyou.accept_with_dates.message', 'Vi har skickat dina föreslagna tider vidare. Nu väntar vi på att din match ska bekräfta vilken tid som passar bäst. Du kommer att få ett meddelande så snart en tid är bestämd!')
      };
    } else if (isAccept && !hasSelectedDates) {
      // User accepted but didn't provide dates
      return {
        icon: <CheckCircle sx={{ fontSize: '4rem', color: 'rgba(255,255,255,0.8)', mb: 2 }} />,
        title: t('thankyou.accept_no_dates.title', 'Tack för ditt svar!'),
        subtitle: t('thankyou.accept_no_dates.subtitle', 'Vi är glada att du vill träffa din match'),
        message: t('thankyou.accept_no_dates.message', 'Eftersom inga specifika tider valdes kommer din match att få föreslå tider, eller så hjälper vår matchmaker till att hitta en tid som passar er båda. Vi hör av oss snart!')
      };
    } else if (isDecline) {
      // User declined
      return {
        icon: <CheckCircle sx={{ fontSize: '4rem', color: 'rgba(255,255,255,0.8)', mb: 2 }} />,
        title: t('thankyou.decline.title', 'Tack för din feedback!'),
        subtitle: t('thankyou.decline.subtitle', 'Vi uppskattar din ärlighet'),
        message: t('thankyou.decline.message', 'Din feedback hjälper oss att förbättra framtida matchningar. Vi fortsätter att hitta rätt person för dig!')
      };
    } else {
      // Default/cancel
      return {
        icon: <CheckCircle sx={{ fontSize: '4rem', color: 'rgba(255,255,255,0.8)', mb: 2 }} />,
        title: t('thankyou.title', 'Tack!'),
        subtitle: t('thankyou.subtitle', 'Tack för ditt svar'),
        message: t('thankyou.message', 'Vi har tagit emot ditt svar och kommer att använda det för att förbättra matchningen. Du kommer att få fler förslag snart!')
      };
    }
  };

  const content = getContent();

  return (
    <>
      <Helmet>
        <title>{content.title} - Relate</title>
        <meta name="description" content={content.subtitle} />
      </Helmet>
      
      <Body>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Box sx={{ 
            textAlign: 'center', 
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4
          }}>
            {content.icon}
            
            <Typography variant="h1" gutterBottom>
              {content.title}
            </Typography>
            
            <Typography variant="h4" sx={{ mb: 3, opacity: 0.9 }}>
              {content.subtitle}
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                lineHeight: 1.8, 
                fontSize: '1.2rem',
                maxWidth: '500px',
                mb: 4
              }}
              dangerouslySetInnerHTML={{ __html: content.message }}
            />
            
            <Button
              variant="contained"
              onClick={handleBackToHome}
              size="large"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                minWidth: '200px',
                '&:hover': { 
                  bgcolor: 'rgba(255,255,255,0.3)',
                  border: '1px solid rgba(255,255,255,0.5)'
                }
              }}
            >
              {t('common.back_to_home', 'Tillbaka till startsidan')}
            </Button>
          </Box>
        </Container>
      </Body>
    </>
  );
} 
